import { useCallback, useEffect, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { RequestStatus } from '../../constants';
import { useBFFContext } from '../context';

export function useStatus() {
	const {
		setValue,
		value: { status, error },
		currentLoadingRequests,
		updateCurrentLoadingRequests,
	} = useBFFContext();

	const isInitializing = useMemo(() => status === RequestStatus.INIT, [status]);
	const isComplete = useMemo(() => status === RequestStatus.COMPLETE, [status]);
	const isError = useMemo(() => status === RequestStatus.ERROR, [status]);
	const isLoading = useMemo(() => status === RequestStatus.LOADING, [status]);
	const isLoaded = useMemo(() => status !== RequestStatus.LOADING, [status]);

	// Globally update the status based on the request queue for the BFF
	useEffect(() => {
		if (!currentLoadingRequests.length && status === RequestStatus.LOADING) {
			// Ensure that the error state remains if it was previously set
			setValue((prev) => {
				if (prev.status === RequestStatus.ERROR) {
					return prev;
				}
				return { ...prev, status: RequestStatus.COMPLETE };
			});
		}
	}, [currentLoadingRequests, setValue, status]);

	// Globally manage the request queue of any calls to the BFF
	const setStatus = useCallback(
		(newStatus: RequestStatus, integratingComponent?: string, error?: Error) => {
			if (newStatus === RequestStatus.LOADING && integratingComponent) {
				updateCurrentLoadingRequests(integratingComponent, RequestStatus.LOADING);
			}

			if (newStatus === RequestStatus.COMPLETE) {
				if (integratingComponent) {
					updateCurrentLoadingRequests(integratingComponent, RequestStatus.COMPLETE);
				}
			}

			if (newStatus === RequestStatus.ERROR) {
				if (integratingComponent) {
					updateCurrentLoadingRequests(integratingComponent, RequestStatus.ERROR);
				}
			}

			setValue((prev) => {
				// Don't allow the status to be marked as anything other than ERROR
				// if an error is pre-existing
				if (
					prev.status === RequestStatus.ERROR &&
					// Put behind FG bc this makes the status unstable between renders
					!fg('navx-1574-navigation-error-boundary-shortcuts')
				) {
					return prev;
				}

				const isErrorStatus = newStatus === RequestStatus.ERROR;

				return {
					...prev,
					status: isErrorStatus ? RequestStatus.ERROR : RequestStatus.LOADING,
					...(fg('navx-1574-navigation-error-boundary-shortcuts')
						? { error: isErrorStatus ? error : undefined }
						: {}),
				};
			});
		},
		[updateCurrentLoadingRequests, setValue],
	);

	return {
		error,
		status,
		isInitializing,
		isComplete,
		isError,
		isLoading,
		isLoaded,
		setStatus,
	};
}
